import { useDrag } from '@use-gesture/react';
import {
  FunctionComponent,
  MouseEventHandler,
  createElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import { DraftConfiguration, Point, Size, ViewMap } from '@/types';
import { trackFeature } from '@/utils/adobeLaunch';
import { getVectorLength } from '@/utils/math/getVectorLength';
import { radToDegree } from '@/utils/math/radToDegree';
import { PinchHandler, usePinchSubscriber } from '@/hooks/usePinch';
import { scaleSize } from '@/model/configuration/scaleSize';
import { getIsSingleConfigurationPrintType } from '@/model/printType/getIsSingleConfigurationPrintType';
import { useReduxDraftDispatch } from '@/redux/draft/draftDispatch';
import { getSortedConfigurationsOnCurrentView } from '@/redux/draft/getters/getSortedConfigurationsOnCurrentView';
import { Delete } from '@/components/common/icons/Delete';
import { Duplicate } from '@/components/common/icons/Duplicate';
import { LayerDown } from '@/components/common/icons/LayerDown';
import { LayerUp } from '@/components/common/icons/LayerUp';
import { Rotate } from '@/components/common/icons/Rotate';
import { useMultiSelectContext } from '../../MultiSelectContext';
import { useDragElement } from '../../RegisterDragElement';
import { duplicateConfigurationAction } from '../../draftState/actions/duplicateConfigurationAction';
import { swapConfigurationAction } from '../../draftState/actions/swapConfigurationAction';
import { useConfigurationSelector } from '../../hooks/useConfigurationSelector';
import { useScaleFactor } from '../../hooks/useScaleFactor';
import { getDeltaFromState } from '../../utils/getDeltaFromState';
import { getVectors } from '../../utils/getVectors';
import { ConfigurationHandle } from '../ConfigurationHandle';
import { ConfigurationMove } from '../ConfigurationMove';
import { MultiSelectHandles } from '../MultiSelectHandles';
import { ConfigurationHandleWithBubble } from './ConfigurationHandleWithBubble';
import './ConfigurationHandlesWithZoom.scss';

interface ActiveConfigurationHandlesWithZoomProps
  extends Pick<
    ConfigurationHandlesWithZoomProps,
    | 'configuration'
    | 'viewMap'
    | 'onDelete'
    | 'onPinch'
    | 'onRotate'
    | 'onScale'
    | 'snapLinesActive'
    | 'onSetSnapLineActive'
    | 'infoText'
  > {
  currentSize: Size;
  isMoving: boolean;
  setIsConfigChangeActive: (isActive: boolean) => void;
}

const ActiveConfigurationHandlesWithZoom: FunctionComponent<
  ActiveConfigurationHandlesWithZoomProps
> = ({
  configuration,
  viewMap,
  onDelete,
  onPinch,
  onRotate,
  onScale,
  snapLinesActive,
  onSetSnapLineActive,
  infoText,
  currentSize,
  isMoving,
  setIsConfigChangeActive,
}) => {
  const dispatch = useReduxDraftDispatch();
  const [setRef, scaleFactor, svgRef] = useScaleFactor(viewMap);
  const [setScaleBottomRightElementRef, scaleBottomRightElementRef] = useDragElement();
  const [setScaleTopRightElementRef, scaleTopRightElementRef] = useDragElement();
  const [setScaleBottomLeftElementRef, scaleBottomLeftElementRef] = useDragElement();
  const [setScaleTopLeftElementRef, scaleTopLeftElementRef] = useDragElement();
  const [setRotateElementRef, rotateElementRef] = useDragElement();
  const designCenter = useRef<Point>();
  const restrict = configuration.restrict;
  const [activeScaleRef, setActiveScaleRef] =
    useState<React.MutableRefObject<HTMLButtonElement | null> | null>(null);
  const [hideHandles, setHideHandles] = useState({ top: false, bottom: false });

  const configurationsOnView = useSelector(getSortedConfigurationsOnCurrentView);
  const indexOnView = configurationsOnView.indexOf(configuration);

  function handleDuplication() {
    trackFeature('duplicate-configuration');
    dispatch(duplicateConfigurationAction(configuration));
  }

  function handleLayerUp() {
    trackFeature('configuration-layer-up');
    const next = configurationsOnView[indexOnView + 1];
    dispatch(swapConfigurationAction(configuration, next));
  }

  function handleLayerDown() {
    trackFeature('configuration-layer-down');
    const next = configurationsOnView[indexOnView - 1];
    dispatch(swapConfigurationAction(configuration, next));
  }

  const createScaleHandler = (elementRef: any) => (state: any) => {
    const isActiveChange = !state.first && !state.last;
    setIsConfigChangeActive(isActiveChange);
    setHideHandles({ top: isActiveChange, bottom: isActiveChange });

    setActiveScaleRef(elementRef);
    if (state.last) {
      setActiveScaleRef(null);
    }
    const [startVector, currentVector] = getVectors(
      state,
      configuration,
      viewMap,
      svgRef,
      designCenter
    );
    if (startVector && currentVector) {
      const currentDistance = getVectorLength(currentVector);
      const startDistance = getVectorLength(startVector);

      const newScale = (currentDistance / startDistance) * configuration.scale;

      onScale(newScale, state.first, state.last, state.dragging);
    }
  };

  useDrag(createScaleHandler(scaleTopRightElementRef), { target: scaleTopRightElementRef });

  useDrag(createScaleHandler(scaleBottomRightElementRef), { target: scaleBottomRightElementRef });

  useDrag(createScaleHandler(scaleBottomLeftElementRef), { target: scaleBottomLeftElementRef });

  useDrag(createScaleHandler(scaleTopLeftElementRef), { target: scaleTopLeftElementRef });

  useDrag(
    (state) => {
      const isActiveChange = !state.first && !state.last;
      setIsConfigChangeActive(isActiveChange);
      setHideHandles({ top: isActiveChange, bottom: false });

      const [startVector, currentVector] = getVectors(
        state,
        configuration,
        viewMap,
        svgRef,
        designCenter
      );
      if (startVector && currentVector) {
        let angleRad =
          Math.atan2(currentVector.y, currentVector.x) - Math.atan2(startVector.y, startVector.x);
        if (angleRad < 0) {
          angleRad += 2 * Math.PI;
        }

        onRotate(radToDegree(angleRad), state.first, state.last, state.dragging);
      }
    },
    { target: rotateElementRef }
  );

  const pinchSubscriber = usePinchSubscriber();
  useEffect(() => {
    if (onPinch) {
      pinchSubscriber.add(onPinch);
      return () => pinchSubscriber.remove(onPinch);
    }
  }, [pinchSubscriber, onPinch]);

  const topHandleGroupWidth =
    configurationsOnView.length > 1 ? scaleFactor * 5.6 : scaleFactor * 2.55;
  const adjustRadius = scaleFactor < 3 ? 0 : 1;
  const adjustStrokeWidth = scaleFactor < 7 ? scaleFactor / 2 : scaleFactor / 1.5;

  return (
    <g ref={setRef}>
      {!restrict?.rotate && !hideHandles.bottom && !isMoving && (
        <g
          transform={`translate(${currentSize.width / 2 - scaleFactor / 2},
          ${currentSize.height + scaleFactor / 1.25})`}
          filter="drop-shadow(2px 4px 6px rgba(0, 0, 0, 0.16))"
        >
          <rect
            x={0}
            y={0}
            rx={adjustRadius}
            ry={adjustRadius}
            vectorEffect="non-scaling-size"
            width={scaleFactor}
            height={scaleFactor}
            stroke="#fff"
            strokeWidth={adjustStrokeWidth}
            fill="#fff"
          />
          <ConfigurationHandle
            ref={setRotateElementRef}
            x={0}
            y={0}
            size={scaleFactor}
            Icon={Rotate}
            name="rotate"
            scaleFactor={scaleFactor}
          />
        </g>
      )}
      {!restrict?.scale && (
        <ConfigurationHandleWithBubble
          ref={setScaleBottomRightElementRef}
          active={activeScaleRef?.current === scaleBottomRightElementRef.current}
          x={currentSize.width - scaleFactor / 2}
          y={currentSize.height - scaleFactor / 2}
          size={scaleFactor}
          name="bottomRightScale"
          scaleFactor={scaleFactor}
        />
      )}
      {!restrict?.scale && (
        <ConfigurationHandleWithBubble
          ref={setScaleTopRightElementRef}
          active={activeScaleRef?.current === scaleTopRightElementRef.current}
          x={currentSize.width - scaleFactor / 2}
          y={-scaleFactor / 2}
          size={scaleFactor}
          name="topRightScale"
          scaleFactor={scaleFactor}
        />
      )}
      {!restrict?.scale && (
        <ConfigurationHandleWithBubble
          ref={setScaleTopLeftElementRef}
          active={activeScaleRef?.current === scaleTopLeftElementRef.current}
          x={-scaleFactor / 2}
          y={-scaleFactor / 2}
          size={scaleFactor}
          name="topLeftScale"
          scaleFactor={scaleFactor}
        />
      )}
      {!restrict?.scale && (
        <ConfigurationHandleWithBubble
          ref={setScaleBottomLeftElementRef}
          active={activeScaleRef?.current === scaleBottomLeftElementRef.current}
          x={-scaleFactor / 2}
          y={currentSize.height - scaleFactor / 2}
          size={scaleFactor}
          name="bottomLeftScale"
          scaleFactor={scaleFactor}
        />
      )}
      {!hideHandles.top && !isMoving && (
        <g
          transform={`translate(${currentSize.width / 2 - topHandleGroupWidth / 2},
          ${-scaleFactor - scaleFactor})`}
          filter="drop-shadow(2px 4px 6px rgba(0, 0, 0, 0.16))"
        >
          <rect
            x={0}
            y={0}
            rx={adjustRadius}
            ry={adjustRadius}
            vectorEffect="non-scaling-size"
            width={topHandleGroupWidth}
            height={scaleFactor}
            stroke="#fff"
            strokeWidth={adjustStrokeWidth}
            fill="#fff"
          />
          {!getIsSingleConfigurationPrintType(configuration.printType) && (
            <ConfigurationHandle
              x={0}
              y={0}
              size={scaleFactor}
              Icon={Duplicate}
              onClick={handleDuplication}
              name="duplicate"
              scaleFactor={scaleFactor}
            />
          )}
          {!restrict?.remove && (
            <ConfigurationHandle
              x={scaleFactor + scaleFactor / 2}
              y={0}
              size={scaleFactor}
              Icon={Delete}
              onClick={onDelete}
              name="delete"
              scaleFactor={scaleFactor}
            />
          )}
          {configurationsOnView.length > 1 && (
            <>
              <ConfigurationHandle
                Icon={LayerUp}
                x={(scaleFactor + scaleFactor / 2) * 2}
                y={0}
                size={scaleFactor}
                onClick={handleLayerUp}
                name="layerUp"
                disabled={indexOnView === configurationsOnView.length - 1}
                scaleFactor={scaleFactor}
              />
              <ConfigurationHandle
                Icon={LayerDown}
                onClick={handleLayerDown}
                x={(scaleFactor + scaleFactor / 2) * 3}
                y={0}
                size={scaleFactor}
                name="layerDown"
                disabled={indexOnView === 0}
                scaleFactor={scaleFactor}
              />
            </>
          )}
        </g>
      )}
      {infoText && scaleFactor > 0 && (
        <g
          className="configuration-info-text"
          filter="drop-shadow(2px 4px 6px rgba(0, 0, 0, 0.16))"
        >
          <text
            filter="url(#bg)"
            x={currentSize.width / 2}
            y={-scaleFactor}
            textAnchor="middle"
            fontSize={scaleFactor}
          >
            {infoText}
          </text>
          <text
            fill="#000"
            x={currentSize.width / 2}
            y={-scaleFactor}
            textAnchor="middle"
            fontSize={scaleFactor}
          >
            {infoText}
          </text>
        </g>
      )}
    </g>
  );
};

interface ConfigurationHandlesWithZoomProps {
  active: boolean;
  configuration: DraftConfiguration;
  infoText: string;
  isSnapTarget: boolean;
  onDelete: MouseEventHandler;
  onMove: (offset: Point, first: boolean, last: boolean, dragging: boolean | undefined) => void;
  onPinch: PinchHandler | undefined;
  onRotate: (
    rotationChange: number,
    first: boolean,
    last: boolean,
    dragging: boolean | undefined
  ) => void;
  onScale: (scale: number, first: boolean, last: boolean, dragging: boolean | undefined) => void;
  onSetSnapLineActive: () => void;
  snapLinesActive: boolean;
  scale: number;
  viewMap: ViewMap;
  hasConfigurationErrors: boolean;
}

const ConfigurationEditHandlesWithZoom: FunctionComponent<ConfigurationHandlesWithZoomProps> = ({
  active,
  configuration,
  infoText,
  isSnapTarget,
  onDelete,
  onMove,
  onPinch,
  onRotate,
  onScale,
  onSetSnapLineActive,
  snapLinesActive,
  scale,
  viewMap,
  hasConfigurationErrors,
}) => {
  const [isConfigChangeActive, setIsConfigChangeActive] = useState(false);
  const [targetRef, selectConfiguration] = useConfigurationSelector<SVGGElement>(
    configuration,
    active
  );
  const isMovable = !configuration.restrict?.move;
  const [isMoving, setIsMoving] = useState(false);

  const [setMoveElementRef, moveElementRef] = useDragElement();

  // needs to be always active so we can move with the same touch that selects the configuration
  useDrag(
    (state) => {
      if (state.touches > 1) {
        return;
      }
      const isActiveChange = !state.first && !state.last;
      setIsConfigChangeActive(isActiveChange);
      setIsMoving(isActiveChange);

      const transformedDelta = getDeltaFromState(state, targetRef);

      onMove(transformedDelta, state.first, state.last, state.dragging);
    },
    { target: moveElementRef, enabled: isMovable }
  );

  const currentSize = scaleSize(configuration.size, scale);

  const boundaryStyles = cn(
    'configuration-handles-zoom__boundary',
    active && 'configuration-handles-zoom__boundary--visible',
    isConfigChangeActive && 'configuration-handles-zoom__boundary--active',
    hasConfigurationErrors && 'configuration-handles-zoom__boundary--invalid',
    isSnapTarget && 'configuration-handles-zoom__boundary--docked'
  );

  return (
    <g ref={targetRef} className="configuration-handles-zoom">
      <rect width={currentSize.width} height={currentSize.height} className={boundaryStyles} />
      <ConfigurationMove
        ref={setMoveElementRef}
        size={currentSize}
        active={isMovable && active}
        onPointerDown={selectConfiguration}
      />
      {active && (
        <ActiveConfigurationHandlesWithZoom
          configuration={configuration}
          viewMap={viewMap}
          onDelete={onDelete}
          onPinch={onPinch}
          onRotate={onRotate}
          onScale={onScale}
          snapLinesActive={snapLinesActive}
          onSetSnapLineActive={onSetSnapLineActive}
          currentSize={currentSize}
          isMoving={isMoving}
          setIsConfigChangeActive={setIsConfigChangeActive}
          infoText={infoText}
        />
      )}
    </g>
  );
};

export const ConfigurationHandlesWithZoom: FunctionComponent<ConfigurationHandlesWithZoomProps> = (
  props
) => {
  const { configurationIds, isModeActive } = useMultiSelectContext();
  if (isModeActive || configurationIds.includes(props.configuration.cid)) {
    return createElement(MultiSelectHandles, props);
  }

  return createElement(ConfigurationEditHandlesWithZoom, props);
};
